import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import type { User, RegisterRequest } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated on mount (cookie will be sent automatically)
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get<User>(`${API_URL}/auth/me`);
      setUser(response.data);
      setIsAdmin(response.data.isAdmin || false);
      setError(null);
    } catch {
      // Not authenticated or error - that's fine
      setUser(null);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      const response = await axios.post<{ user: User }>(`${API_URL}/auth/login`, {
        email,
        password
      });

      // Cookie is set by server, just update state
      const newUser = response.data.user;
      setUser(newUser);
      setIsAdmin(newUser.isAdmin || false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error during login';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      setError(null);
      const response = await axios.post<{ user: User }>(`${API_URL}/auth/register`, data);

      // Cookie is set by server, just update state
      const newUser = response.data.user;
      setUser(newUser);
      setIsAdmin(newUser.isAdmin || false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error during registration';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie on server
      await axios.post(`${API_URL}/auth/logout`);
    } catch {
      // Ignore errors, clear state anyway
    }
    setUser(null);
    setIsAdmin(false);
    setError(null);
    // Redirect to public login page
    window.location.href = '/public/login';
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin,
    login,
    register,
    logout,
    isLoading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
